import { useEffect, useState } from 'react';
import { useNCZIContext } from '../Services/NCZIContext';
import useNCZICommands from './useNCZICommands';
import apiClient from '../../../Services/ApiClient';
import { FinishedCommand } from '../Commands/Command';
import { NCZIEhrExtractEnvelope } from '../Interfaces/ncziTypes';
import {
	GetPatientExaminationList,
	GetPatientExaminationListParams,
} from '../Commands/GetPatientExaminationList';
import { ElectronicExaminationFilterValues } from '../Components/ElectronicExamination/Filter/ElectronicExaminationFilter';
import { NCZIElectronicExaminationRecordType } from '../Enums/ncziElectronicExaminationRecordType.enum';

export default function useNCZIElectronicExamination() {
	const {
		patientExaminationData,
		setPatientExaminationData,
		setPatientExaminationPage,
		patientIdentifier,
	} = useNCZIContext();

	const { commands, execute } = useNCZICommands();

	const [listExaminationCommandId, setListExaminationCommandId] = useState<
		string | undefined
	>(undefined);
	const [loadingCommandId, setLoadingCommandId] = useState<string | undefined>(
		undefined,
	);

	const fetchPatientElectronicExaminationData = async (
		params: GetPatientExaminationListParams,
	) => {
		const command = await obtainPatientElectronicExaminationListData(params);
		setPatientExaminationData(null);
		return command;
	};

	useEffect(() => {
		if (listExaminationCommandId && !patientExaminationData) {
			const command = commands[listExaminationCommandId];
			if (command && command.state === 'done') {
				const finishedCommand = command as FinishedCommand;
				if (finishedCommand.result) {
					setPatientExaminationData(
						finishedCommand.result as NCZIEhrExtractEnvelope,
					);
				}
			}
		}
	}, [commands, listExaminationCommandId]);

	const obtainPatientElectronicExaminationListData = async (
		params: GetPatientExaminationListParams,
	) => {
		const commandGetPatientExaminationList = GetPatientExaminationList(params);

		if (params?.page) {
			setPatientExaminationPage(params.page);
		}
		const result = await execute(commandGetPatientExaminationList);
		if (!('error' in result && result.error)) {
			setListExaminationCommandId(result.id);
		}

		return result;
	};

	const handleLoadPatientExaminationData = async (
		page: number = 1,
		filterValues?: ElectronicExaminationFilterValues,
	) => {
		if (patientIdentifier) {
			const filterTypes = [];
			if (filterValues?.typesZoov) {
				filterTypes.push(
					NCZIElectronicExaminationRecordType.PROFESSIONAL_EXAMINATION,
				);
			}
			if (filterValues?.typesZps) {
				filterTypes.push(NCZIElectronicExaminationRecordType.RELEASE_REPORT);
			}
			if (filterValues?.typesZozv) {
				filterTypes.push(
					NCZIElectronicExaminationRecordType.IMAGING_EXAMINATION,
				);
			}

			let sensitivity = undefined;
			const sensitivityValue = filterValues?.sensitivity?.value?.toString();
			if (sensitivityValue) {
				const intSensitivity = parseInt(sensitivityValue);
				if (intSensitivity > 0 && intSensitivity < 6) {
					sensitivity = intSensitivity;
				}
			}

			const command = await fetchPatientElectronicExaminationData({
				patientCode: patientIdentifier,
				page,
				dateFrom: filterValues?.dateFrom?.format('YYYY-MM-DD'),
				dateTo: filterValues?.dateTo?.format('YYYY-MM-DD'),
				ownRecords: filterValues?.ownRecords,
				types: filterTypes.length ? filterTypes : undefined,
				sensitivity,
				expertise: filterValues?.expertise?.value || undefined,
			});
			if (command.id) {
				setLoadingCommandId(command.id);
			}
		}
	};

	const handleFakeLoadPatientExaminationData = async () => {
		const { data: response } = await apiClient.get(
			'/nczi/message/03744a24-dd7c-4316-9bd3-d052d59c7ce8/response',
		);
		setPatientExaminationPage(1);
		setPatientExaminationData(response.data);
	};

	return {
		handleLoadPatientExaminationData,
		handleFakeLoadPatientExaminationData,
		loadingCommandId,
	};
}
